import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu'
import { CheckIcon } from 'flyt/ui/Icons/Icons'
import type {
  DropdownMenuCheckboxItemProps,
  DropdownMenuItemProps,
  DropdownMenuLabelProps,
  DropdownMenuProps,
} from './DropdownMenu.types'

/**
 * dropdown-menu — menu-overlay composite.
 *
 * The caller's trigger gets the menu-trigger behavior merged onto it
 * (as-child, no wrapper element); the panel portals to the document body,
 * end-aligned beneath the trigger with a small offset and a minimum width.
 * Open state is self-managed by the confined library: standard dismissal
 * (Escape / outside interaction) closes it and returns focus to the trigger.
 * @radix-ui/react-dropdown-menu is confined to this unit.
 */
export function DropdownMenu({ trigger, children }: DropdownMenuProps) {
  return (
    <RadixDropdownMenu.Root>
      <RadixDropdownMenu.Trigger asChild>{trigger}</RadixDropdownMenu.Trigger>
      <RadixDropdownMenu.Portal>
        <RadixDropdownMenu.Content
          align="end"
          sideOffset={4}
          /* AN OVERLAY THAT CHROME CAN COVER IS NOT AN OVERLAY. The panel
             portals to the body, but a portal alone does not win the paint
             order: any positioned chrome carrying a z-index (the desktop menu
             column, the mobile drawer) would paint straight over a panel left
             at the default. It therefore stacks at the app's overlay level,
             and among equals the portal's position at the end of the body
             settles it — a menu opened FROM chrome must still be readable. */
          className="z-50 min-w-40 rounded-lg border border-border bg-surface p-1 shadow-overlay"
        >
          {children}
        </RadixDropdownMenu.Content>
      </RadixDropdownMenu.Portal>
    </RadixDropdownMenu.Root>
  )
}

/**
 * Non-interactive heading row labelling the items beneath it. Renders as a
 * muted, uppercase group label with a divider below, exposed to assistive
 * technology as a label rather than a menu item.
 */
export function DropdownMenuLabel({ children }: DropdownMenuLabelProps) {
  return (
    <RadixDropdownMenu.Label className="mb-1 border-b border-border px-2 pt-1 pb-2 text-xs font-semibold tracking-wide text-text-muted uppercase">
      {children}
    </RadixDropdownMenu.Label>
  )
}

/**
 * Controlled checkbox menu-item row. Activation reports the intended next
 * value exactly once (coerced to a strict boolean) and does NOT dismiss the
 * menu, so several rows can be toggled in one visit. The row exposes
 * checkbox-menu-item semantics with its checked state; the leading indicator
 * box is decorative and hidden from assistive technology, showing the check
 * glyph only while checked.
 */
export function DropdownMenuCheckboxItem({
  checked,
  onCheckedChange,
  children,
}: DropdownMenuCheckboxItemProps) {
  return (
    <RadixDropdownMenu.CheckboxItem
      checked={checked}
      onCheckedChange={(value) => onCheckedChange(value === true)}
      onSelect={(event) => event.preventDefault()}
      className="group flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-text outline-none select-none data-highlighted:bg-surface-muted"
    >
      <span
        aria-hidden
        className="flex size-4 shrink-0 items-center justify-center rounded-sm border border-border-control bg-surface group-data-[state=checked]:border-accent group-data-[state=checked]:bg-accent group-data-[state=checked]:text-accent-contrast"
      >
        <RadixDropdownMenu.ItemIndicator className="flex items-center justify-center">
          <CheckIcon width={12} height={12} />
        </RadixDropdownMenu.ItemIndicator>
      </span>
      {children}
    </RadixDropdownMenu.CheckboxItem>
  )
}

/**
 * Plain single-select menu row. Activation fires `onSelect` exactly once and
 * dismisses the menu (the library's standard single-select behavior), with
 * focus returning to the trigger. The row keeps plain menu-item semantics
 * (role `menuitem`) and never announces as a checkbox; when `selected`, its
 * current-selection state is exposed to assistive technology via
 * `aria-current`. A fixed-width leading indicator carries the check glyph only
 * while selected (decorative, hidden from assistive technology) and otherwise
 * stays empty to keep labels aligned.
 */
export function DropdownMenuItem({ onSelect, selected, children }: DropdownMenuItemProps) {
  return (
    <RadixDropdownMenu.Item
      onSelect={() => onSelect()}
      aria-current={selected === true ? true : undefined}
      className="group flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-text outline-none select-none data-highlighted:bg-surface-muted"
    >
      <span aria-hidden className="flex size-4 shrink-0 items-center justify-center">
        {selected === true ? <CheckIcon width={12} height={12} /> : null}
      </span>
      {children}
    </RadixDropdownMenu.Item>
  )
}

/**
 * A hairline between two GROUPS OF ROWS THAT ANSWER DIFFERENT QUESTIONS —
 * "which one of these" above it, "do this" below it. It is decorative and
 * exposed to assistive technology as a separator, never as a menu item, so
 * keyboard navigation walks straight past it. Without it the caller would draw
 * its own line inside a panel it does not own.
 */
export function DropdownMenuSeparator() {
  return <RadixDropdownMenu.Separator className="my-1 h-px bg-border" />
}
